import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import { getShows, uploadExcel } from "../services/api";
import { AlertTriangle, CheckCircle2, FolderOpen, XCircle } from "lucide-react";

const F = "'Montserrat',sans-serif";


const STATUS_STYLE = {
  active:        { bg: "#f0fdf4", color: "#16a34a", label: "Active" },
  awaiting_upload:{ bg: "#fef4e6", color: "#C9760A", label: "Pending Upload" },
  inactive:      { bg: "#F4F4F5", color: "#818084", label: "Inactive" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.inactive;
  return (
    <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700,
      background: s.bg, color: s.color, letterSpacing: 0.3 }}>
      {s.label}
    </span>
  );
}

function JobResult({ result }) {
  const ok = result.failed_rows === 0;
  return (
    <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8,
      background: ok ? "#f0fdf4" : "#fdecec", border: `1px solid ${ok ? "#bbf7d0" : "#fbd5d5"}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: ok ? "#16a34a" : "#C1121F", display: "flex", alignItems: "center", gap: 4 }}>
        {ok ? <><CheckCircle2 size={13}/> Upload successful</> : <><AlertTriangle size={13}/> Upload completed with errors</>}
      </div>
      <div style={{ fontSize: 10, color: "#818084", marginTop: 4, lineHeight: 1.6 }}>
        <span style={{ marginRight: 12 }}>Rows loaded: <b>{result.loaded_rows}</b></span>
        {result.failed_rows > 0 && <span style={{ marginRight: 12 }}>Failed: <b>{result.failed_rows}</b></span>}
        <span>Job ID: <b style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9 }}>{result.job_id}</b></span>
      </div>
      <div style={{ fontSize: 10, color: "#818084", marginTop: 2 }}>
        Status: <b>{result.status}</b> · Scheduled: {new Date(result.scheduled_at).toLocaleString()}
      </div>
    </div>
  );
}

function ShowUploadRow({ show, year, currentUser }) {
  const fileRef = useRef();
  const [state, setState]     = useState("idle"); // idle | uploading | done | error
  const [result, setResult]   = useState(null);
  const [errMsg, setErrMsg]   = useState("");

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setState("uploading");
    setResult(null);
    setErrMsg("");
    try {
      const res = await uploadExcel(file, show.ite_event_id, show.show_id, currentUser || "data-admin", "tonight");
      setResult(res);
      setState("done");
    } catch (err) {
      setErrMsg(err.message || "Upload failed");
      setState("error");
    }
    e.target.value = "";
  };

  return (
    <div style={{ padding: "16px 20px", borderBottom: "1px solid #F4F4F5",
      background: "#fff", borderRadius: 10, marginBottom: 8,
      boxShadow: "0 1px 3px rgba(16,24,40,0.04)" }}>

      {/* Show header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#26262A" }}>{show.name}</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <StatusChip status={show.status} />

          {/* Year badge — one row per year, fixed */}
          <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
            background: "#FCEBE5", color: "#E8431A", border: "1px solid #F7C9B8" }}>
            {year}
          </span>

          {/* Hidden file input */}
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }}
            onChange={handleFile} />

          {/* Upload button */}
          <button
            onClick={() => fileRef.current.click()}
            disabled={state === "uploading"}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none",
              background: state === "uploading" ? "#D8DADC" : "#E8431A",
              color: state === "uploading" ? "#818084" : "#fff",
              fontSize: 11, fontWeight: 700, cursor: state === "uploading" ? "default" : "pointer",
              fontFamily: F, display: "flex", alignItems: "center", gap: 4 }}>
            {state === "uploading" ? "Uploading…" : <><FolderOpen size={14} style={{marginRight:4}}/> Upload Excel</>}
          </button>
        </div>
      </div>

      {/* Result / error */}
      {state === "done"  && result && <JobResult result={result} />}
      {state === "error" && (
        <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8,
          background: "#fdecec", border: "1px solid #fbd5d5", fontSize: 11, color: "#C1121F", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 4 }}>
          <XCircle size={13}/> {errMsg}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const {
    selectedEvent, changeEvent, events: allEvents = [], nav,
    currentRole, setCurrentRole, currentUser,
    notifs, setNotifs, notifOpen, setNotifOpen,
  } = useApp();

  const [shows, setShows]     = useState([]);
  const [loading, setLoading] = useState(false);
  // Default view is the "pending uploads" queue; the toggle reveals already-processed
  // shows so more companies can be topped up onto an existing show.
  const [showProcessed, setShowProcessed] = useState(false);

  useEffect(() => {
    setLoading(true);
    getShows()
      .then(data => setShows(data || []))
      .catch(() => setShows([]))
      .finally(() => setLoading(false));
  }, []);

  // A show is "processed" once it has companies or a completed job; "running" while enriching.
  const isProcessed = (s) =>
    s.latest_job_status === "completed" || (s.total_companies || 0) > 0;
  const isRunning = (s) => s.latest_job_status === "enriching";

  const visibleShows = shows.filter(s =>
    showProcessed ? isProcessed(s) : (!isProcessed(s) && !isRunning(s))
  );

  // Expand each show into one entry per tracked year
  const showYearRows = visibleShows.flatMap(s => {
    const years = s.tracked_years
      ? s.tracked_years.split(",").map(y => parseInt(y.trim())).filter(Boolean).sort((a,b) => a - b)
      : [new Date().getFullYear()];
    return years.map(y => ({ show: s, year: y }));
  });

  const ev = allEvents.find(e => e.ite_event_id === selectedEvent);

  return (
    <PageShell>

      <Sidebar active="admin" onNav={nav}
        selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}/>

      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

        {/* Top bar */}
        <div style={{ padding:"16px 28px", background:"#fff", borderBottom:"1px solid #F4F4F5",
          display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0,
          boxShadow:"0 1px 3px rgba(16,24,40,0.03)" }}>
          <div>
            <div style={{ fontSize:19, fontWeight:800, letterSpacing:-0.4 }}>Data Admin</div>
            <div style={{ fontSize:11, color:"#AFABAB", marginTop:1 }}>
              Upload exhibitor Excel files to trigger enrichment
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ display:"flex", background:"#F4F4F5", borderRadius:8, padding:3 }}>
              {[["pending","Pending uploads"],["processed","Processed shows"]].map(([key,label]) => {
                const active = (key === "processed") === showProcessed;
                return (
                  <button key={key} onClick={() => setShowProcessed(key === "processed")}
                    style={{ padding:"6px 14px", borderRadius:6, border:"none",
                      background: active ? "#fff" : "transparent",
                      color: active ? "#26262A" : "#AFABAB",
                      fontSize:11, fontWeight: active ? 700 : 500, cursor:"pointer", fontFamily:F,
                      boxShadow: active ? "0 1px 3px rgba(16,24,40,0.12)" : "none" }}>
                    {label}
                  </button>
                );
              })}
            </div>
            <span style={{ fontSize:11, color:"#AFABAB" }}>
              {showYearRows.length} {showProcessed ? "processed" : "pending"}
            </span>
          </div>
        </div>

        <div style={{ flex:1, overflow:"auto", padding:"28px 32px 48px" }}>


          {/* Loading */}
          {loading && (
            <div style={{ textAlign:"center", padding:"60px 20px", color:"#AFABAB" }}>
              <div style={{ fontSize:13 }}>Loading shows…</div>
            </div>
          )}

          {/* Empty state */}
          {!loading && showYearRows.length === 0 && (
            <div style={{ textAlign:"center", padding:"60px 20px", color:"#AFABAB" }}>
              <div style={{ marginBottom:12 }}><CheckCircle2 size={48} color="#D8DADC"/></div>
              <div style={{ fontSize:15, fontWeight:700, color:"#3F3F46" }}>
                {showProcessed ? "No processed shows yet" : "No pending uploads"}
              </div>
              <div style={{ fontSize:12, marginTop:4 }}>
                {showProcessed
                  ? "Shows appear here once they've been enriched."
                  : "All shows for this event have data uploaded, or no shows have been added yet."}
              </div>
              <button onClick={() => nav("portfolio")}
                style={{ marginTop:16, padding:"8px 18px", borderRadius:8, border:"1px solid #D8DADC",
                  background:"#fff", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:F, color:"#3F3F46" }}>
                Go to Competitor Tracker →
              </button>
            </div>
          )}

          {/* Shows list */}
          {!loading && showYearRows.length > 0 && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"#AFABAB", letterSpacing:0.5,
                textTransform:"uppercase", marginBottom:12 }}>
                {showProcessed ? "Processed" : "Pending Upload"} — {showYearRows.length} edition{showYearRows.length !== 1 ? "s" : ""}
              </div>
              {showProcessed && (
                <div style={{ display:"flex", alignItems:"flex-start", gap:8, padding:"10px 14px",
                  marginBottom:12, background:"#fef4e6", border:"1px solid #F7C9B8", borderRadius:8,
                  fontSize:11, color:"#8a5a12", lineHeight:1.5 }}>
                  <AlertTriangle size={14} style={{ flexShrink:0, marginTop:1 }} color="#C9760A"/>
                  <span>Uploading to a processed show <b>appends</b> companies — it does not replace or
                  de-duplicate. Upload only companies not already in the show.</span>
                </div>
              )}
              {showYearRows.map(({show, year}) => <ShowUploadRow key={`${show.show_id}-${year}`} show={show} year={year} currentUser={currentUser} />)}
            </div>
          )}

        </div>
      </div>
    </PageShell>
  );
}
